import {
  DetailedHTMLProps,
  HTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from "react";
import React from "react";
import {
  PillsInput,
  Pill,
  Combobox,
  CheckIcon,
  Group,
  useCombobox,
  ComboboxProps,
} from "@mantine/core";
import { useReduxAppSelector } from "src/services/redux/ReduxStore";
import groupService from "src/services/core/group/Group.service";
import { timeWindowGroupsService } from "src/services/core/time-window-group/TimeWindowGroups.service";

type SelectOption = {
  name: string;
  idOnDevice: number;
};

function getDisplayName(option: TimeWindowGroup): string {
  return `${option.idOnDevice}${
    option?.name != null && option.name.trim() != "" ? `. ${option.name}` : ""
  }`;
}

function TimeWindowGroupDropDown(
  props: {
    selected: number;
    setSelected: (value: number) => void;
    comboboxProps?: ComboboxProps | undefined;
  } & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>
) {
  useEffect(() => {
    (async () => {
      const groupResult = await groupService.getSelectedGroup();
      if (!groupResult.success || groupResult.value == null) {
        return;
      }

      timeWindowGroupsService.get(groupResult.value.id);
    })();
  }, []);

  const preTransformTimeWindowGroupList =
    useReduxAppSelector((s) => s.timeWindowGroupSlice) ?? [];
  const {
    selected: selectedIdOnDevice,
    setSelected,
    comboboxProps,
    ...divProps
  } = props;

  const timeWindowGroupList = [...Array(64).keys()].map((i) => {
    const idOnDevice = i + 1;
    const twg = preTransformTimeWindowGroupList.find(
      (twg) => twg.idOnDevice == idOnDevice
    );
    return {
      name: twg == null ? idOnDevice.toString() : getDisplayName(twg),
      idOnDevice,
    };
  });
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });
  const [search, setSearch] = useState("");
  const options = timeWindowGroupList
    .filter((timeWindowGroup) => timeWindowGroup.name.includes(search))
    .map((timeWindowGroup) => (
      <Combobox.Option
        value={timeWindowGroup.name}
        key={timeWindowGroup.idOnDevice}
        active={selectedIdOnDevice == timeWindowGroup.idOnDevice}
      >
        <Group gap="sm">
          <span>{timeWindowGroup.name}</span>
          {selectedIdOnDevice == timeWindowGroup.idOnDevice ? (
            <CheckIcon size={12} />
          ) : null}
        </Group>
      </Combobox.Option>
    ));
  const selectedValue = useMemo((): SelectOption => {
    return (
      timeWindowGroupList.find(
        (twg) => twg.idOnDevice == selectedIdOnDevice
      ) ?? {
        name: "",
        idOnDevice: 0,
      }
    );
  }, [timeWindowGroupList, selectedIdOnDevice]);

  return (
    <div {...divProps}>
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          setSelected(parseInt(val));
          combobox.closeDropdown();
        }}
        {...comboboxProps}
      >
        <Combobox.DropdownTarget>
          <PillsInput onClick={() => combobox.openDropdown()}>
            <Pill.Group>
              <Pill>{selectedValue.name}</Pill>
              <Combobox.EventsTarget>
                <PillsInput.Field
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={search}
                  placeholder="Search values"
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && search.length === 0) {
                      event.preventDefault();
                      setSelected(0);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>

        <Combobox.Dropdown>
          <Combobox.Options mah={200} style={{ overflowY: "auto" }}>
            {options}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}

export default TimeWindowGroupDropDown;
